import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Crown, Shield, Mail, Calendar, ChevronRight, Sparkles, Eye } from "lucide-react";
import { useAuth } from "../AuthContext";
import { ProAccessModal } from "../components/ProAccessModal";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showAccess, setShowAccess] = useState(false);

  if (!user) return null;
  const isPro = user.plan === "pro";
  const used = user.free_scans_used ?? 0;
  const limit = user.free_scans_limit ?? 3;
  const bonus = user.bonus_scans ?? 0;
  const total = limit + bonus;
  const remaining = Math.max(0, total - used);
  const pct = isPro ? 100 : Math.min(100, total === 0 ? 0 : (used / total) * 100);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen pb-24 px-5 sm:px-8 pt-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Eye size={18} className="text-[#DEFF9A]" />
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#DEFF9A]">
          Cuenta
        </span>
      </div>
      <h1 className="font-display text-4xl tracking-tighter mb-8">Perfil</h1>

      <div className="bg-[#0A0A0A] border border-[#1F2428] rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-[#DEFF9A] flex items-center justify-center font-display text-xl text-black">
            {(user.name?.[0] || "U").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-lg truncate">{user.name}</div>
            <div className="text-xs text-[#8A8F98] flex items-center gap-1.5 truncate">
              <Mail size={11} /> {user.email}
            </div>
          </div>
          {isPro && (
            <div data-testid="pro-badge" className="px-2.5 py-1 rounded-full bg-[#DEFF9A]/15 border border-[#DEFF9A]/40 flex items-center gap-1">
              <Crown size={12} className="text-[#DEFF9A]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#DEFF9A]">PRO</span>
            </div>
          )}
        </div>

        <div className="bg-[#141414] rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#8A8F98]">
              {isPro ? "Plan Pro · Ilimitado" : `${used} / ${total} análisis usados${bonus > 0 ? ` · +${bonus} pack` : ""}`}
            </span>
            <span className="font-mono text-xs font-bold text-[#DEFF9A]">
              {isPro ? "∞" : `${remaining} restantes`}
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#1F2428] overflow-hidden">
            <div
              data-testid="usage-bar"
              className="h-full bg-[#DEFF9A] transition-all duration-700"
              style={{ width: `${pct}%`, boxShadow: "0 0 8px rgba(222,255,154,0.5)" }}
            />
          </div>
        </div>
      </div>

      <button
        data-testid="profile-upgrade-btn"
        onClick={() => setShowAccess(true)}
        className="w-full bg-[#0A0A0A] border border-[#DEFF9A]/40 hover:border-[#DEFF9A] rounded-2xl p-5 mb-6 flex items-center gap-4 transition-all text-left glow-acid"
      >
        <div className="w-12 h-12 rounded-xl bg-[#DEFF9A]/15 flex items-center justify-center">
          <Sparkles size={20} className="text-[#DEFF9A]" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg tracking-tight">ScanEye Professional</div>
          <div className="text-xs text-[#8A8F98]">Análisis ilimitados · Informes periciales certificados</div>
        </div>
        <ChevronRight size={18} className="text-[#8A8F98]" />
      </button>

      <ul className="bg-[#0A0A0A] border border-[#1F2428] rounded-2xl divide-y divide-[#1F2428] mb-6">
        <li className="p-4 flex items-center gap-3">
          <Shield size={16} className="text-[#8A8F98]" />
          <span className="flex-1 text-sm">Seguridad biométrica</span>
          <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#8BF56C]">ACTIVA</span>
        </li>
        <li className="p-4 flex items-center gap-3">
          <Calendar size={16} className="text-[#8A8F98]" />
          <span className="flex-1 text-sm">Miembro desde</span>
          <span className="text-xs font-mono text-[#8A8F98]">
            {new Date(user.created_at).toLocaleDateString("es-ES")}
          </span>
        </li>
      </ul>

      <button
        data-testid="logout-btn"
        onClick={handleLogout}
        className="w-full bg-transparent border border-[#1F2428] hover:border-[#FF5A6E] hover:text-[#FF5A6E] text-[#8A8F98] font-medium rounded-lg py-3 transition-colors flex items-center justify-center gap-2"
      >
        <LogOut size={16} />
        Cerrar sesión
      </button>

      <ProAccessModal
        open={showAccess}
        onClose={() => setShowAccess(false)}
        source="profile_upgrade"
      />
    </div>
  );
}
